import { get, set, createStore } from "idb-keyval";
import { EditableRetreatScheduleSchema } from "@retreat/core";
import type { EditableRetreatSchedule } from "@retreat/core";
import { createChildLogger } from "@retreat/shared";
import { STORAGE_KEYS } from "../constants";
import { reportError } from "../utils/reportError";

const log = createChildLogger("userScheduleRepository");

// Dedicated IndexedDB store — keeps user schedules isolated from the default idb-keyval store.
// Exported so unit tests can reset via del(key, scheduleStore) using the same connection handle.
export const scheduleStore = createStore("rc-user-schedules", "schedules");

const IDB_KEY = STORAGE_KEYS.USER_SCHEDULES;

// ── Types ─────────────────────────────────────────────────────────────────────

export type UserSchedule = {
  schedule: EditableRetreatSchedule;
  // null = built from scratch; UUID string = forked from a default schedule
  baseScheduleId: string | null;
  isDeleted: boolean;
  createdAt: number;
  updatedAt: number;
};

// ── Read ──────────────────────────────────────────────────────────────────────

export async function loadUserSchedules(): Promise<UserSchedule[]> {
  try {
    const raw = await get<unknown>(IDB_KEY, scheduleStore);
    if (!Array.isArray(raw)) return [];
    const valid: UserSchedule[] = [];
    for (const item of raw) {
      if (!isRawUserSchedule(item)) continue;
      const parsed = EditableRetreatScheduleSchema.safeParse(item.schedule);
      if (!parsed.success) {
        // Legacy data written before saveUserSchedule validated on write (or corrupted some
        // other way). Must not crash the Library screen, but must be visible to us — a schedule
        // dropping out of the list with only a console warning is exactly the bug this guards.
        reportError(new Error(parsed.error.message), "SCHEDULE_ENTRY_INVALID");
        log.warn("Skipping corrupt user schedule entry", {
          id: item.schedule?.id,
        });
        continue;
      }
      valid.push({ ...item, schedule: parsed.data });
    }
    return valid.filter((s) => !s.isDeleted);
  } catch (err) {
    // Read failures degrade to an empty list (indistinguishable from "no schedules yet" in the
    // UI) rather than crashing the Library screen — but must still be visible to us.
    reportError(err, "SCHEDULE_LOAD_FAILED");
    log.warn("loadUserSchedules failed", { error: String(err) });
    return [];
  }
}

// ── Write ─────────────────────────────────────────────────────────────────────

export async function saveUserSchedule(entry: UserSchedule): Promise<void> {
  // Validate before persisting so a malformed schedule (e.g. a container missing its
  // type's required field) fails loudly right here — surfaced to the user via the editor's
  // save-error banner — instead of writing successfully and only being discovered (and
  // silently dropped) the next time loadUserSchedules() runs.
  const parsed = EditableRetreatScheduleSchema.safeParse(entry.schedule);
  if (!parsed.success) {
    const err = new Error(parsed.error.message);
    reportError(err, "SCHEDULE_SAVE_INVALID");
    log.warn("saveUserSchedule rejected invalid schedule", {
      id: entry.schedule?.id,
      error: parsed.error.message,
    });
    throw err;
  }
  const validatedEntry: UserSchedule = { ...entry, schedule: parsed.data };

  const all = await loadAllRaw();
  const idx = all.findIndex(
    (s) => s.schedule?.id === validatedEntry.schedule.id,
  );
  if (idx >= 0) {
    all[idx] = validatedEntry;
  } else {
    all.push(validatedEntry);
  }
  try {
    await set(IDB_KEY, all, scheduleStore);
    log.info("User schedule saved", { id: entry.schedule.id });
  } catch (err) {
    // Must not swallow this: callers rely on the promise rejecting to know the save didn't
    // actually happen (a save that silently no-ops looks identical to a successful one).
    reportError(err, "SCHEDULE_SAVE_FAILED");
    log.warn("saveUserSchedule failed (IndexedDB unavailable?)", {
      error: String(err),
    });
    throw err;
  }
}

// ── Soft delete ───────────────────────────────────────────────────────────────

export async function deleteUserSchedule(scheduleId: string): Promise<void> {
  const all = await loadAllRaw();
  const idx = all.findIndex((s) => s.schedule?.id === scheduleId);
  if (idx < 0) return;
  all[idx] = { ...all[idx], isDeleted: true, updatedAt: Date.now() };
  try {
    await set(IDB_KEY, all, scheduleStore);
    log.info("User schedule soft-deleted", { id: scheduleId });
  } catch (err) {
    reportError(err, "SCHEDULE_DELETE_FAILED");
    log.warn("deleteUserSchedule failed", { error: String(err) });
    throw err;
  }
}

// ── Internal ──────────────────────────────────────────────────────────────────

async function loadAllRaw(): Promise<UserSchedule[]> {
  try {
    const raw = await get<unknown>(IDB_KEY, scheduleStore);
    return Array.isArray(raw) ? (raw as UserSchedule[]) : [];
  } catch {
    return [];
  }
}

function isRawUserSchedule(v: unknown): v is UserSchedule {
  return (
    typeof v === "object" &&
    v !== null &&
    "schedule" in v &&
    "isDeleted" in v &&
    "createdAt" in v &&
    "updatedAt" in v
  );
}
